// src/features/media/stores/mergeTrackingStore.ts

import { create } from 'zustand';
import { notifications } from '@mantine/notifications';

export interface PendingMerge {
  mediaId: number;
  title: string;
}

/**
 * Toast identity shared by the initial "Merging..." toast (VideosPage) and
 * every job-poll update (useMergeJobPolling) — one id per merge target so
 * updates replace instead of stack.
 */
export class MergeToast {
  static id(mediaId: number): string {
    return `merge-${mediaId}`;
  }
}

interface MergeTrackingState {
  pendingMerges: PendingMerge[];
}

interface MergeTrackingActions {
  addMerge: (merge: PendingMerge) => void;
  removeMerge: (mediaId: number) => void;
  /**
   * Drop all tracking AND the toasts it owns. For customer switch: the job
   * poll runs against the new account and can never observe the old
   * account's merge jobs, so their "Merging..." toasts would spin forever.
   */
  clearWithToasts: () => void;
}

/**
 * Pending merge targets, tracked app-wide. Module-level (not page state) on
 * purpose: merge toasts live in the global notification provider and their
 * completion updates come from job polling — page-local state orphaned the
 * toasts the moment the user navigated away from /videos, leaving permanent
 * "Merging..." spinners the user had to dismiss by hand.
 */
export const useMergeTrackingStore = create<MergeTrackingState & MergeTrackingActions>()(
  (set, get) => ({
    pendingMerges: [],

    addMerge: (merge) =>
      set((state) => ({ pendingMerges: [...state.pendingMerges, merge] })),

    removeMerge: (mediaId) =>
      set((state) => ({
        pendingMerges: state.pendingMerges.filter((pm) => pm.mediaId !== mediaId),
      })),

    clearWithToasts: () => {
      for (const pm of get().pendingMerges) {
        notifications.hide(MergeToast.id(pm.mediaId));
      }
      set({ pendingMerges: [] });
    },
  })
);
